import { FormikProps, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { MdChevronLeft } from 'react-icons/md';
import { mutate } from 'swr';
import { StoreDetailsStore } from 'zustand-store';
import { consignmentService } from 'services';
import { useAuth, useScreenInfo } from 'hooks';
import {
  ConsignmentDetails,
  FormOption,
  PointOfSale,
  PointOfSaleSubmit,
} from 'types';
import {
  Accordion,
  Button,
  EmployeeManager,
  Heading,
  Input,
  Select,
  Text,
} from 'components';
import {
  getDataLayer,
  getLocalStorageItem,
  messages,
  setLocalStorageItem,
  storedAttendance,
  swrKeys,
} from 'helpers';
import {
  FontSize,
  FontWeight,
  INVOICE_OPTIONS,
  LocalStorageKeys,
  TEST_IDS,
  salesMode,
} from 'commons';
import { AutomaticOptions } from './components';
import * as S from './styles';
import { ConfirmAttendanceModal, PrintLabel } from '../../../components';

interface Props {
  consignment: ConsignmentDetails;
  isPickupInStore: boolean;
  setShowRefuse: (value: boolean) => void;
}

interface InvoiceForm {
  invoice: string;
  invoiceOption: string;
  pointOfSale: PointOfSale;
  submitType: PointOfSaleSubmit;
  employeeDocument: string | null;
}

const INVOICE_SELECT_OPTIONS: FormOption[] = [
  {
    label: 'Chave da NF',
    value: INVOICE_OPTIONS.invoiceKey,
    testId: TEST_IDS.INVOICE_CODE_OPTION_KEY,
  },
  {
    label: 'Número da NF',
    value: INVOICE_OPTIONS.invoiceNumber,
    testId: TEST_IDS.INVOICE_CODE_OPTION_NUMBER,
  },
];

const ONLY_ONE_POINT_OF_SALE_TYPE = 1;

export function ConfirmInvoice({
  consignment,
  isPickupInStore,
  setShowRefuse,
}: Props) {
  const { storeDetails } = StoreDetailsStore.useGetStoreDetails();
  const { taxCouponPointOfSale, invoicePointOfSale, manualPointOfSale } =
    storeDetails;

  const { user } = useAuth();
  const { isAdmin } = user;

  const { isMobile } = useScreenInfo();
  const { code, entries, reservedPOS, distance } = consignment;

  const [forceOldFlow, setForceOldFlow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [employeeList, setEmployeeList] = useState<FormOption[]>(
    getLocalStorageItem(LocalStorageKeys.EmployeeData, [])
  );

  const currentAttendance = useMemo(() => {
    return storedAttendance.getCurrent({
      entries,
      consignmentCode: code,
    });
  }, [code, entries]);

  function persistInvoice(invoice: string) {
    const newAttendance = { ...currentAttendance, invoice };

    storedAttendance.persist(newAttendance);
  }

  const storedInvoiceOption: string = getLocalStorageItem(
    LocalStorageKeys.InvoiceOption,
    INVOICE_OPTIONS.invoiceKey
  );

  const {
    values,
    errors,
    handleChange,
    setFieldValue,
    handleSubmit,
    submitForm,
  }: FormikProps<InvoiceForm> = useFormik<InvoiceForm>({
    initialValues: {
      invoice: currentAttendance.invoice || '',
      invoiceOption: storedInvoiceOption,
      pointOfSale: PointOfSale.Manual,
      submitType: PointOfSaleSubmit.Manual,
      employeeDocument: null,
    },

    onSubmit: async (values) => {
      setIsSubmitting(true);

      const allowSubmitting =
        values.invoice !== '' || values.submitType !== PointOfSaleSubmit.Manual;

      if (!allowSubmitting) {
        setIsSubmitting(false);

        messages([{ errorCode: 'FRONT-RE-INVOICE_KEY_NOT_INFORMED' }]);

        return;
      }

      const { invoice, invoiceOption, employeeDocument } = values;
      const currentAttendance = storedAttendance.getCurrent({
        entries,
        consignmentCode: code,
      });

      const eanList = entries.map((entry) => {
        const storedProduct = currentAttendance?.products.find(
          (attendanceProduct) => {
            return entry.product.code === attendanceProduct.code;
          }
        );

        return storedProduct?.ean || '';
      });

      let productEans: { product: string; ean: string }[] = [];

      entries?.forEach((entry, index) => {
        const { legacySKU } = entry.product;

        productEans = [
          ...productEans,
          { product: legacySKU, ean: eanList[index] },
        ];
      });

      const invoiceValue = invoice === '' ? undefined : invoice;

      const result = await consignmentService.putConfirm({
        consignmentCode: code,
        productEans,
        nfeKey:
          invoiceOption === INVOICE_OPTIONS.invoiceKey &&
          values.submitType === PointOfSaleSubmit.Manual
            ? invoiceValue
            : undefined,
        nfeNumber:
          invoiceOption === INVOICE_OPTIONS.invoiceNumber &&
          values.submitType === PointOfSaleSubmit.Manual
            ? invoiceValue
            : undefined,
        pointOfSale: values.pointOfSale,
        store: reservedPOS.name,
        employeeDocument,
      });

      const store = reservedPOS.name;

      function sendInvoiceCompletionAnalysis() {
        const deliveryType = consignment.deliveryMode.salesMode;

        if (deliveryType !== salesMode.pickup) return;

        const invoiceType = invoiceOption;
        const invoiceCharactersQuantity = String(invoiceValue?.length || 0);

        const dataLayer = getDataLayer();

        dataLayer.push({
          event: 'invoice_form_submit',
          store,
          invoice_type: invoiceType,
          invoice_characters_quantity: invoiceCharactersQuantity,
        });
      }

      if (result?.status === 200) {
        sendInvoiceCompletionAnalysis();

        mutate(swrKeys.consignments({ key: store }));
      } else {
        closeModal();
        setIsSubmitting(false);
      }
    },
  });

  function changeInvoiceOption(event: any) {
    const { target } = event;

    if (target) {
      const invoiceOption = target.value;

      setLocalStorageItem(LocalStorageKeys.InvoiceOption, invoiceOption);

      handleChange(event);
    }
  }

  const initialSelected = useMemo(() => {
    return INVOICE_SELECT_OPTIONS.find(
      (option) => option.value === values.invoiceOption
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignment]);

  function openModal() {
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setFieldValue('submitType', PointOfSaleSubmit.Manual);
  }

  function automaticSubmit({
    pointOfSale,
    submitType,
  }: {
    pointOfSale: PointOfSale | null;
    submitType: PointOfSaleSubmit;
  }) {
    if (pointOfSale) {
      setFieldValue('pointOfSale', pointOfSale);
      setFieldValue('submitType', submitType);
      openModal();
    } else {
      setFieldValue('pointOfSale', manualPointOfSale);
      setForceOldFlow(true);
    }
  }

  function renderEmployeeList() {
    const shouldRenderEmployeeSelect =
      taxCouponPointOfSale === PointOfSale.Cigam ||
      taxCouponPointOfSale === PointOfSale.CigamMeiaSola ||
      taxCouponPointOfSale === PointOfSale.Seta ||
      invoicePointOfSale === PointOfSale.Cigam ||
      invoicePointOfSale === PointOfSale.CigamMeiaSola ||
      invoicePointOfSale === PointOfSale.Seta;

    if (!shouldRenderEmployeeSelect) return;

    const hasRegisteredEmployee = Boolean(employeeList.length);

    return (
      <div className="flex flex-col gap-2">
        <Text>
          Selecione o vendedor responsável pelo pedido para que o documento
          possa ser gerado:
        </Text>

        <div className="xl-items-center flex w-full flex-col items-end gap-3 xl:flex-row">
          {hasRegisteredEmployee && (
            <div className="flex w-full xl:max-w-72">
              <Select.Wrapper
                key={JSON.stringify(employeeList)}
                name="employeeDocument"
                options={employeeList}
                onChange={handleChange}
              >
                <Select.Input placeholder="Selecione vendedor" />

                <Select.StatusArrow />
              </Select.Wrapper>
            </div>
          )}

          <EmployeeManager
            employeeList={employeeList}
            setEmployeeList={setEmployeeList}
          />
        </div>
      </div>
    );
  }

  useEffect(() => {
    setFieldValue('employeeDocument', null);
  }, [employeeList, setFieldValue, forceOldFlow]);

  if ((taxCouponPointOfSale || invoicePointOfSale) && !forceOldFlow) {
    const isOnlyOneType =
      [taxCouponPointOfSale, invoicePointOfSale, manualPointOfSale].reduce(
        (acc, cur) => (cur ? acc + 1 : acc),
        0
      ) === ONLY_ONE_POINT_OF_SALE_TYPE;

    let titleText = 'Emissão fiscal';
    let introText =
      'Realize a emissão da nota fiscal ou cupom fiscal para seguir com o atendimento.';
    let infoText = 'automaticamente após sua Nota ou Cupom Fiscal ser emitido.';

    if (isOnlyOneType) {
      titleText = `Emissão ${
        taxCouponPointOfSale ? 'do cupom' : 'da nota'
      } fiscal`;
      introText = `Realize a emissão ${
        taxCouponPointOfSale ? 'do cupom' : 'da nota'
      } fiscal para seguir com o atendimento.`;
      infoText = `automaticamente após ${
        taxCouponPointOfSale
          ? 'seu Cupom Fiscal ser emitido.'
          : 'sua Nota Fiscal ser emitida.'
      }`;
    }

    return (
      <Accordion title={titleText} defaultOpen>
        {showModal && (
          <ConfirmAttendanceModal
            closeModal={closeModal}
            isAdmin={isAdmin}
            isInvoice={values.submitType === PointOfSaleSubmit.Invoice}
            isPickupInStore={isPickupInStore}
            isSubmitting={isSubmitting}
            submitForm={submitForm}
          />
        )}

        <S.AutomaticFlowWrapper>
          <S.AutomaticFlowContainer>
            <S.TextWrapper>
              <Text>{introText}</Text>

              {renderEmployeeList()}

              {!isOnlyOneType && (
                <div className="flex flex-col gap-2">
                  <span className="font-bold">
                    Selecione o formato de emissão
                  </span>

                  <Text>
                    {isPickupInStore
                      ? 'A cliente será acionada'
                      : 'Uma transportadora já será acionada para envio do pedido'}{' '}
                    {infoText}
                  </Text>
                </div>
              )}
            </S.TextWrapper>

            <div className="flex flex-col gap-8">
              <AutomaticOptions
                automaticSubmit={automaticSubmit}
                distance={distance}
                invoicePointOfSale={invoicePointOfSale}
                isAdmin={isAdmin}
                isOnlyOneType={isOnlyOneType}
                isSubmitting={isSubmitting}
                manualPointOfSale={manualPointOfSale}
                taxCouponPointOfSale={taxCouponPointOfSale}
                isPickupInStore={isPickupInStore}
              />

              <div className="flex flex-col justify-between gap-2 xl:flex-row xl:items-center xl:pr-6">
                <p className="text-sm">
                  Em caso de problemas, você ainda pode recusar este pedido
                </p>

                <div className="w-full xl:w-unset">
                  <Button
                    data-testid={TEST_IDS.REFUSE_ATTENDANCE_BUTTON}
                    variant="outline-danger"
                    disabled={isSubmitting || isAdmin}
                    type="button"
                    onClick={() => setShowRefuse(true)}
                    full
                  >
                    Recusar Pedido
                  </Button>
                </div>
              </div>
            </div>
          </S.AutomaticFlowContainer>

          {Boolean(!taxCouponPointOfSale || !invoicePointOfSale) &&
            !isMobile && <PrintLabel consignment={consignment} />}
        </S.AutomaticFlowWrapper>
      </Accordion>
    );
  }

  return (
    <>
      <S.Form onSubmit={handleSubmit}>
        {(taxCouponPointOfSale || invoicePointOfSale) && (
          <S.BackButton onClick={() => setForceOldFlow(false)} type="button">
            <MdChevronLeft size={24} /> Voltar
          </S.BackButton>
        )}
        <S.InputContainer>
          <S.Wrapper>
            <Heading size={FontSize.font16} weight={FontWeight.bold}>
              Emissão da nota fiscal
            </Heading>

            <Text size={FontSize.font12}>
              Realize a emissão da nota fiscal e adicione o código no campo
              abaixo. Ao continuar,{' '}
              {false
                ? 'uma transportadora já será acionada para envio do pedido'
                : 'o cliente sera comunicado que o pedido esta disponível para retirada'}
            </Text>
          </S.Wrapper>

          <S.Wrapper>
            <S.Label htmlFor="invoice">Código da nota fiscal:</S.Label>

            <S.InvoiceWrapper>
              <Select.Wrapper
                initialSelected={initialSelected}
                name="invoiceOption"
                onChange={changeInvoiceOption}
                options={INVOICE_SELECT_OPTIONS}
                disabled={isAdmin}
              >
                <Select.Input />

                <Select.StatusArrow />
              </Select.Wrapper>

              <Input
                onBlur={() => persistInvoice(values.invoice || '')}
                name="invoice"
                type="tel"
                placeholder={
                  values.invoiceOption === 'invoice_key'
                    ? 'Digite a chave da NF aqui'
                    : 'Digite o número da NF aqui'
                }
                onChange={(event) => {
                  event.target.value = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  );

                  handleChange(event);
                }}
                value={values.invoice}
                status={errors.invoice ? 'error' : 'default'}
                disabled={isAdmin}
              />
            </S.InvoiceWrapper>
          </S.Wrapper>
        </S.InputContainer>

        {!isMobile && <PrintLabel consignment={consignment} />}

        <S.ButtonsWrapper>
          <Button
            data-testid={TEST_IDS.REFUSE_ATTENDANCE_BUTTON}
            variant="outline-secondary"
            disabled={isSubmitting || isAdmin}
            type="button"
            onClick={() => setShowRefuse(true)}
          >
            Recusar pedido
          </Button>

          <Button
            data-testid={TEST_IDS.CONFIRM_INVOICE_BUTTON}
            type="submit"
            disabled={isSubmitting || isAdmin}
          >
            {isPickupInStore ? 'Comunicar Cliente' : 'Acionar Transportador'}
          </Button>
        </S.ButtonsWrapper>
      </S.Form>
    </>
  );
}
